import React, { useState, useEffect, useRef } from 'react';
import {
  Plus, Edit2, Trash2, Eye, Save, X, Calendar, Tag, Image as ImageIcon,
  User, Clock, Search, Filter, Upload, Check, AlertCircle, ChevronDown,
  FileText, Settings, Globe, Facebook, Twitter, Linkedin, BarChart3, Languages,
  Bold, Italic, Underline, List, ListOrdered, AlignLeft, AlignCenter, AlignRight,
  Link as LinkIcon, Code, Quote, Loader, Users
} from 'lucide-react';
import './BlogBackoffice.css';
import UserManagement from './UserManagement';

import AnalyticsDashboard from './components/Analytics/AnalyticsDashboard';


import { ArticleService, AuthorService, CategoryService, LanguageService, TagService } from './apiService';

const BlogBackoffice = () => {

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // États principaux
  const [articles, setArticles] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentView, setCurrentView] = useState('list'); // 'list', 'edit', 'preview'
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterLanguage, setFilterLanguage] = useState('all');
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);


  // États du formulaire
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    image: '/0a.png',
    imagePreview: '/0a.png',
    imageArticle: '',
    imageArticlePreview: '',
    publish_date: new Date().toISOString().split('T')[0],
    author_id: '',
    tags: [],
    category_id: '',
    status: 'draft', // 'draft', 'published', 'scheduled'
    read_time: '5 min read',
    language_id: '',
    meta_description: '',
    meta_keywords: ''
  });

  const [currentTag, setCurrentTag] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [translatingArticle, setTranslatingArticle] = useState(null);
  const [showTranslationModal, setShowTranslationModal] = useState(false);
  const [targetLanguage, setTargetLanguage] = useState('');

  const imageInputRef = useRef(null);
  const imageArticleInputRef = useRef(null);
  const contentEditorRef = useRef(null);
  const editorImageInputRef = useRef(null);

  // Plateformes de partage
  const sharePlatforms = [
    { id: 'facebook', name: 'Facebook', icon: Facebook },
    { id: 'twitter', name: 'Twitter', icon: Twitter },
    { id: 'linkedin', name: 'LinkedIn', icon: Linkedin }
  ];

  // Charger toutes les données au démarrage
  useEffect(() => {
    loadAllData();
  }, []);

  // Synchroniser le contenu de l'éditeur lors de la modification d'un article
  useEffect(() => {
    if (contentEditorRef.current && formData.content) {
      contentEditorRef.current.innerHTML = formData.content;
    }
  }, [selectedArticle]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      // Charger toutes les données en parallèle
      const [articlesData, authorsData, categoriesData, languagesData, tagsData] = await Promise.all([
        ArticleService.getAll({
          status: filterStatus !== 'all' ? filterStatus : undefined,
          category_id: filterCategory !== 'all' ? filterCategory : undefined,
          language_id: filterLanguage !== 'all' ? filterLanguage : undefined,
          search: searchTerm || undefined
        }),
        AuthorService.getAll(),
        CategoryService.getAll(),
        LanguageService.getAll(),
        TagService.getAll()
      ]);

      // Debug - voir la structure des données
      console.log('Articles reçus:', articlesData);
      console.log('Auteurs reçus:', authorsData);
      console.log('Catégories reçues:', categoriesData);
      console.log('Langues reçues:', languagesData);

      // Extraire les données avec la structure réelle de l'API
      // Articles peuvent être dans data.data (avec pagination) ou directement dans data
      const articlesArray = articlesData.data?.data || articlesData.data || [];
      const authorsArray = authorsData.data?.data || authorsData.data || [];
      const categoriesArray = categoriesData.data?.data || categoriesData.data || [];
      const languagesArray = languagesData.data?.data || languagesData.data || [];
      const tagsArray = tagsData.data?.data || tagsData.data || [];

      setArticles(Array.isArray(articlesArray) ? articlesArray : []);
      setAuthors(Array.isArray(authorsArray) ? authorsArray : []);
      setCategories(Array.isArray(categoriesArray) ? categoriesArray : []);
      setLanguages(Array.isArray(languagesArray) ? languagesArray : []);
      setTags(Array.isArray(tagsArray) ? tagsArray : []);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      setShowErrorMessage('Erreur lors du chargement des données. Veuillez réessayer.');
      // Initialiser avec des tableaux vides en cas d'erreur
      setArticles([]);
      setAuthors([]);
      setCategories([]);
      setLanguages([]);
      setTags([]);
    } finally {
      setLoading(false);
    }
  };

  const loadArticles = async () => {
    setLoading(true);
    try {
      const response = await ArticleService.getAll({
        status: filterStatus !== 'all' ? filterStatus : undefined,
        category_id: filterCategory !== 'all' ? filterCategory : undefined,
        language_id: filterLanguage !== 'all' ? filterLanguage : undefined,
        search: searchTerm || undefined
      });

      // Extraire les articles avec la structure réelle de l'API (avec pagination)
      const articlesArray = response.data?.data || response.data || [];
      setArticles(Array.isArray(articlesArray) ? articlesArray : []);
    } catch (error) {
      console.error('Erreur lors du chargement des articles:', error);
      setShowErrorMessage('Erreur lors du chargement des articles.');
      setArticles([]);
    } finally {
      setLoading(false);
    }
  };

  // Compression et redimensionnement d'image en 16:9
  const compressImage = (file, maxWidth = 1200) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;

        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          // Ratio 16:9
          const targetRatio = 16 / 9;
          let width = img.width;
          let height = img.height;
          const currentRatio = width / height;

          // Ajuster pour obtenir 16:9
          if (currentRatio > targetRatio) {
            width = height * targetRatio;
          } else {
            height = width / targetRatio;
          }

          // Redimensionner si nécessaire
          if (width > maxWidth) {
            height = (maxWidth / width) * height;
            width = maxWidth;
          }

          canvas.width = width;
          canvas.height = height;

          // Centrer l'image
          const offsetX = (img.width - (width / (height / img.height))) / 2;
          const offsetY = (img.height - (height / (width / img.width))) / 2;

          ctx.drawImage(
            img,
            Math.max(0, offsetX),
            Math.max(0, offsetY),
            Math.min(img.width, width / (height / img.height)),
            Math.min(img.height, height / (width / img.width)),
            0,
            0,
            width,
            height
          );

          canvas.toBlob((blob) => {
            resolve(blob);
          }, 'image/jpeg', 0.85);
        };

        img.onerror = reject;
      };

      reader.onerror = reject;
    });
  };

  const handleImageUpload = async (e, type = 'main') => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const compressedBlob = await compressImage(file);
      const reader = new FileReader();

      reader.onloadend = () => {
        if (type === 'main') {
          setFormData(prev => ({
            ...prev,
            image: reader.result,
            imagePreview: reader.result
          }));
        } else if (type === 'article') {
          setFormData(prev => ({...prev, imageArticle: reader.result, imageArticlePreview: reader.result}));
        } else if (type === 'editor') {
          insertImageIntoEditor(reader.result);
        }
      };

      reader.readAsDataURL(compressedBlob);
    } catch (error) {
      console.error('Erreur lors de la compression:', error);
      setShowErrorMessage('Erreur lors du traitement de l\'image');
    }
  };

  const insertImageIntoEditor = (imageData) => {
    if (!contentEditorRef.current) return;

    const img = document.createElement('img');
    img.src = imageData;
    img.style.maxWidth = '100%';
    img.style.height = 'auto';
    img.style.margin = '1rem 0';
    img.style.borderRadius = 'var(--bo-radius-md)';

    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      range.insertNode(img);

      const newRange = document.createRange();
      newRange.setStartAfter(img);
      newRange.collapse(true);
      selection.removeAllRanges();
      selection.addRange(newRange);
    } else {
      contentEditorRef.current.appendChild(img);
    }

    setFormData(prev => ({
      ...prev,
      content: contentEditorRef.current.innerHTML
    }));
  };

  const handleEditorCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    if (contentEditorRef.current) {
      setFormData(prev => ({
        ...prev,
        content: contentEditorRef.current.innerHTML
      }));
    }
  };

  const handleContentChange = () => {
    if (contentEditorRef.current) {
      setFormData(prev => ({
        ...prev,
        content: contentEditorRef.current.innerHTML
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.title.trim()) {
      errors.title = 'Le titre est requis';
    }

    if (!formData.excerpt.trim()) {
      errors.excerpt = 'L\'extrait est requis';
    }

    if (!formData.content.trim() || formData.content === '<br>') {
      errors.content = 'Le contenu est requis';
    }

    if (!formData.category_id) {
      errors.category_id = 'La catégorie est requise';
    }

    // L'auteur n'est plus obligatoire (champ masqué côté front)

    if (!formData.language_id) {
      errors.language_id = 'La langue est requise';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setShowErrorMessage('Veuillez corriger les erreurs dans le formulaire');
      return;
    }

    setLoading(true);
    try {
      // Préparer les données pour l'API
      const articleData = {
        title: formData.title,
        excerpt: formData.excerpt,
        content: formData.content,
        image: formData.image,
        imageArticle: formData.imageArticle,
        publish_date: formData.publish_date,
        date: formData.publish_date,
        author_id: parseInt(formData.author_id) || (authors[0] && authors[0].id) || null,
        category_id: parseInt(formData.category_id),
        language_id: parseInt(formData.language_id),
        status: formData.status,
        read_time: formData.read_time,
        meta_description: formData.meta_description || formData.excerpt,
        meta_keywords: formData.meta_keywords || formData.tags.join(', '),
        tags: formData.tags
      };

      if (selectedArticle) {
        // Mise à jour
        await ArticleService.update(selectedArticle.id, articleData);
        setShowSuccessMessage('Article mis à jour avec succès !');
      } else {
        // Création
        await ArticleService.create(articleData);
        setShowSuccessMessage('Article créé avec succès !');
      }

      // Recharger les articles
      await loadArticles();

      // Revenir à la liste après un délai
      setTimeout(() => {
        setShowSuccessMessage(false);
        resetForm();
        setCurrentView('list');
      }, 2000);

    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      setShowErrorMessage(
        error.response?.data?.message ||
        'Erreur lors de la sauvegarde de l\'article. Veuillez réessayer.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (article) => {
    setLoading(true);
    try {
      // Charger l'article complet avec tous les détails
      const fullArticle = await ArticleService.getById(article.id);

      setSelectedArticle(fullArticle.data);

      // Extraire les noms des tags si tags est un tableau d'objets
      const tagsArray = fullArticle.data.tags || [];
      const tagNames = Array.isArray(tagsArray)
        ? tagsArray.map(tag => typeof tag === 'string' ? tag : tag.name)
        : [];

      setFormData({
        title: fullArticle.data.title || '',
        excerpt: fullArticle.data.excerpt || '',
        content: fullArticle.data.content || '',
        image: fullArticle.data.image || '/0a.png',
        imagePreview: fullArticle.data.image || '/0a.png',
        imageArticle: fullArticle.data.imageArticle || '',
        imageArticlePreview: fullArticle.data.imageArticle || '',
        publish_date: fullArticle.data.publish_date?.split('T')[0] || new Date().toISOString().split('T')[0],
        author_id: fullArticle.data.author_id || '',
        tags: tagNames,
        category_id: fullArticle.data.category_id || '',
        status: fullArticle.data.status || 'draft',
        read_time: fullArticle.data.read_time || '5 min read',
        language_id: fullArticle.data.language_id || '',
        meta_description: fullArticle.data.meta_description || '',
        meta_keywords: fullArticle.data.meta_keywords || ''
      });
      setCurrentView('edit');
    } catch (error) {
      console.error('Erreur lors du chargement de l\'article:', error);
      setShowErrorMessage('Erreur lors du chargement de l\'article');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet article ?')) {
      return;
    }

    setLoading(true);
    try {
      await ArticleService.delete(id);
      setShowSuccessMessage('Article supprimé avec succès !');
      await loadArticles();

      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 2000);
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      setShowErrorMessage('Erreur lors de la suppression de l\'article');
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (article) => {
    setLoading(true);
    try {
      const fullArticle = await ArticleService.getById(article.id);
      setSelectedArticle(fullArticle.data);
      setCurrentView('preview');
    } catch (error) {
      console.error('Erreur lors du chargement de l\'article:', error);
      setShowErrorMessage('Erreur lors du chargement de l\'article');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      excerpt: '',
      content: '',
      image: '/0a.png',
      imagePreview: '/0a.png',
      imageArticle: '',
      imageArticlePreview: '',
      publish_date: new Date().toISOString().split('T')[0],
      author_id: '',
      tags: [],
      category_id: '',
      status: 'draft',
      read_time: '5 min read',
      language_id: '',
      meta_description: '',
    meta_keywords: ''
    });
    setSelectedArticle(null);
    setValidationErrors({});
    if (contentEditorRef.current) {
      contentEditorRef.current.innerHTML = '';
    }
  };

  const handleAddTag = () => {
    if (currentTag.trim() && !formData.tags.includes(currentTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, currentTag.trim()]
      }));
      setCurrentTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const openTranslationModal = (article) => {
    setTranslatingArticle(article);
    setShowTranslationModal(true);
    setTargetLanguage(languages.find(lang => lang.id !== article.language_id)?.id || '');
  };

  const startManualTranslation = async () => {
    if (!translatingArticle || !targetLanguage) return;

    try {
      const fullArticle = await ArticleService.getById(translatingArticle.id);

      // Extraire les noms des tags
      const tagsArray = fullArticle.data.tags || [];
      const tagNames = Array.isArray(tagsArray)
        ? tagsArray.map(tag => typeof tag === 'string' ? tag : tag.name)
        : [];

      setFormData({
        title: `[À traduire] ${fullArticle.data.title}`,
        excerpt: fullArticle.data.excerpt,
        content: fullArticle.data.content,
        image: fullArticle.data.image,
        imagePreview: fullArticle.data.image,
        imageArticle: fullArticle.data.imageArticle || '',
        imageArticlePreview: fullArticle.data.imageArticle || '',
        publish_date: new Date().toISOString().split('T')[0],
        author_id: fullArticle.data.author_id,
        tags: tagNames,
        category_id: fullArticle.data.category_id,
        status: 'draft',
        read_time: fullArticle.data.read_time,
        language_id: targetLanguage,
        meta_description: '',
    meta_keywords: ''
      });

      setSelectedArticle(null);
      setShowTranslationModal(false);
      setCurrentView('edit');
    } catch (error) {
      console.error('Erreur lors de la préparation de la traduction:', error);
      setShowErrorMessage('Erreur lors de la préparation de la traduction');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { label: 'Brouillon', className: 'bo-status-draft' },
      published: { label: 'Publié', className: 'bo-status-published' },
      scheduled: { label: 'Programmé', className: 'bo-status-scheduled' }
    };

    const config = statusConfig[status] || statusConfig.draft;
    return <span className={`bo-status-badge ${config.className}`}>{config.label}</span>;
  };

  const getAuthorName = (authorId) => {
    const author = authors.find(a => a.id === authorId);
    return author ? author.name : 'Inconnu';
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? category.name : 'Non catégorisé';
  };

  const getLanguageName = (languageId) => {
    const language = languages.find(l => l.id === languageId);
    return language ? language.name : '';
  };

  // Filtrer les articles - s'assurer que articles est un tableau
  const filteredArticles = Array.isArray(articles) ? articles.filter(article => {
    const matchesSearch = article.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          article.excerpt?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || article.status === filterStatus;
    const matchesCategory = filterCategory === 'all' || article.category_id === parseInt(filterCategory);
    const matchesLanguage = filterLanguage === 'all' || article.language_id === parseInt(filterLanguage);

    return matchesSearch && matchesStatus && matchesCategory && matchesLanguage;
  }) : [];

  // Statistiques - s'assurer que articles est un tableau
  const stats = {
    total: Array.isArray(articles) ? articles.length : 0,
    published: Array.isArray(articles) ? articles.filter(a => a.status === 'published').length : 0,
    draft: Array.isArray(articles) ? articles.filter(a => a.status === 'draft').length : 0,
    scheduled: Array.isArray(articles) ? articles.filter(a => a.status === 'scheduled').length : 0
  };

  // Vue Liste
  const renderListView = () => (
    <div className="bo-list-view">
      <div className="bo-list-header">
        <div className="bo-list-stats">
          <div className="bo-stat-card">
            <div className="bo-stat-icon">
              <FileText size={24} />
            </div>
            <div className="bo-stat-content">
              <span className="bo-stat-value">{stats.total}</span>
              <span className="bo-stat-label">Total</span>
            </div>
          </div>
          <div className="bo-stat-card">
            <div className="bo-stat-icon bo-stat-published">
              <Check size={24} />
            </div>
            <div className="bo-stat-content">
              <span className="bo-stat-value">{stats.published}</span>
              <span className="bo-stat-label">Publiés</span>
            </div>
          </div>
          <div className="bo-stat-card">
            <div className="bo-stat-icon bo-stat-draft">
              <Edit2 size={24} />
            </div>
            <div className="bo-stat-content">
              <span className="bo-stat-value">{stats.draft}</span>
              <span className="bo-stat-label">Brouillons</span>
            </div>
          </div>
          <div className="bo-stat-card">
            <div className="bo-stat-icon bo-stat-scheduled">
              <Clock size={24} />
            </div>
            <div className="bo-stat-content">
              <span className="bo-stat-value">{stats.scheduled}</span>
              <span className="bo-stat-label">Programmés</span>
            </div>
          </div>
        </div>

        <button className="bo-btn bo-btn-primary" onClick={() => { resetForm(); setCurrentView('edit'); }}>
          <Plus size={20} />
          Nouvel article
        </button>
      </div>

      <div className="bo-filters">
        <div className="bo-search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Rechercher un article..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="bo-filter-group">
          <div className="bo-filter-item">
            <Filter size={18} />
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="all">Tous les statuts</option>
              <option value="published">Publiés</option>
              <option value="draft">Brouillons</option>
              <option value="scheduled">Programmés</option>
            </select>
          </div>

          <div className="bo-filter-item">
            <Tag size={18} />
            <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
              <option value="all">Toutes les catégories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          <div className="bo-filter-item">
            <Globe size={18} />
            <select value={filterLanguage} onChange={(e) => setFilterLanguage(e.target.value)}>
              <option value="all">Toutes les langues</option>
              {languages.map(lang => (
                <option key={lang.id} value={lang.id}>{lang.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="bo-loading">
          <Loader size={40} className="bo-spinner" />
          <p>Chargement...</p>
        </div>
      ) : (
        <div className="bo-articles-grid">
          {filteredArticles.length === 0 ? (
            <div className="bo-empty-state">
              <FileText size={64} />
              <h3>Aucun article trouvé</h3>
              <p>Commencez par créer votre premier article</p>
              <button className="bo-btn bo-btn-primary" onClick={() => { resetForm(); setCurrentView('edit'); }}>
                <Plus size={20} />
                Créer un article
              </button>
            </div>
          ) : (
            filteredArticles.map(article => (
              <div key={article.id} className="bo-article-card">
                <div className="bo-article-image" style={{ backgroundImage: `url(${article.imageArticle || article.image})` }}>
                  <div className="bo-article-overlay">
                    {getStatusBadge(article.status)}
                  </div>
                </div>

                <div className="bo-article-content">
                  <div className="bo-article-meta">
                    <span className="bo-article-category">{getCategoryName(article.category_id)}</span>
                    <span className="bo-article-language">{getLanguageName(article.language_id)}</span>
                  </div>

                  <h3 className="bo-article-title">{article.title}</h3>
                  <p className="bo-article-excerpt">{article.excerpt}</p>

                  <div className="bo-article-footer">
                    {/* Auteur masqué */}
                    <div className="bo-article-date">
                      <Calendar size={16} />
                      <span>{formatDate(article.publish_date)}</span>
                    </div>
                  </div>

                  {article.tags && article.tags.length > 0 && (
                    <div className="bo-article-tags">
                      {article.tags.slice(0, 3).map((tag, index) => {
                        const tagName = typeof tag === 'string' ? tag : tag.name;
                        return <span key={index} className="bo-tag-small">{tagName}</span>;
                      })}
                      {article.tags.length > 3 && <span className="bo-tag-more">+{article.tags.length - 3}</span>}
                    </div>
                  )}
                </div>

                <div className="bo-article-actions">
                  <button className="bo-btn-icon" onClick={() => handleView(article)} title="Voir">
                    <Eye size={18} />
                  </button>
                  <button className="bo-btn-icon" onClick={() => handleEdit(article)} title="Modifier">
                    <Edit2 size={18} />
                  </button>
                  <button className="bo-btn-icon" onClick={() => openTranslationModal(article)} title="Traduire">
                    <Languages size={18} />
                  </button>
                  <button className="bo-btn-icon bo-btn-danger" onClick={() => handleDelete(article.id)} title="Supprimer">
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );

  // Vue Édition
  const renderEditView = () => (
    <div className="bo-edit-view">
      <div className="bo-edit-header">
        <button className="bo-btn bo-btn-back" onClick={() => setCurrentView('list')}>
          <X size={20} />
          Retour
        </button>
        <h2>{selectedArticle ? 'Modifier l\'article' : 'Nouvel article'}</h2>
        <div className="bo-edit-actions">
          <button className="bo-btn bo-btn-secondary" onClick={() => setCurrentView('preview')}>
            <Eye size={20} />
            Prévisualiser
          </button>
          <button
            className="bo-btn bo-btn-publish"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader size={20} className="bo-spinner" />
                Enregistrement...
              </>
            ) : (
              <>
                <Save size={20} />
                {selectedArticle ? 'Mettre à jour' : 'Publier'}
              </>
            )}
          </button>
        </div>
      </div>

      <div className="bo-edit-container">
        <div className="bo-edit-main">
          {/* Image principale */}
          <div className="bo-form-group">
            <label className="bo-form-label">
              <ImageIcon size={18} />
              Image principale (16:9)
            </label>
            <div className="bo-image-upload">
              <div
                className="bo-image-preview"
                style={{ backgroundImage: `url(${formData.imagePreview})` }}
                onClick={() => imageInputRef.current?.click()}
              >
                <div className="bo-image-overlay">
                  <Upload size={32} />
                  <span>Cliquez pour changer l'image</span>
                </div>
              </div>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                onChange={(e) => handleImageUpload(e, 'main')}
                style={{ display: 'none' }}
              />
            </div>

          {/* Image de l'article (Thumbnail) */}
          <div className="bo-form-group">
            <label className="bo-form-label">
              <ImageIcon size={18} />
              Image de l'article (Thumbnail)
            </label>
            <div className="bo-image-upload">
              <div
                className="bo-image-preview"
                style={{ backgroundImage: `url(${formData.imageArticlePreview || '/0a.png'})`, height: '200px', backgroundSize: 'contain', backgroundRepeat: 'no-repeat', backgroundPosition: 'center' }}
                onClick={() => imageArticleInputRef.current?.click()}
              >
                <div className="bo-image-overlay">
                  <Upload size={32} />
                  <span>Image pour la liste des articles</span>
                </div>
              </div>
              <input
                ref={imageArticleInputRef}
                type="file"
                accept="image/*"
                onChange={(e) => handleImageUpload(e, 'article')}
                style={{ display: 'none' }}
              />
            </div>
            <small className="bo-form-help">
              Apparaît dans la liste et en haut de l'article (300px)
            </small>
          </div>


          {validationErrors.image && (
              <span className="bo-error-message">{validationErrors.image}</span>
            )}
          </div>




          {/* Titre */}
          <div className="bo-form-group">
            <label className="bo-form-label">Titre de l'article *</label>
            <input
              type="text"
              className={`bo-form-input ${validationErrors.title ? 'bo-input-error' : ''}`}
              value={formData.title}
              onChange={(e) => setFormData(prev => ({...prev, title: e.target.value}))}
              placeholder="Entrez le titre de votre article..."
            />
            {validationErrors.title && (
              <span className="bo-error-message">{validationErrors.title}</span>
            )}
          </div>

          {/* Extrait */}
          <div className="bo-form-group">
            <label className="bo-form-label">Extrait *</label>
            <textarea
              className={`bo-form-textarea ${validationErrors.excerpt ? 'bo-input-error' : ''}`}
              value={formData.excerpt}
              onChange={(e) => setFormData(prev => ({ ...prev, excerpt: e.target.value }))}
              placeholder="Court résumé de l'article..."
              rows={3}
            />
            {validationErrors.excerpt && (
              <span className="bo-error-message">{validationErrors.excerpt}</span>
            )}
          </div>

          {/* Éditeur de contenu */}
          <div className="bo-form-group">
            <label className="bo-form-label">Contenu *</label>
            <div className="bo-editor">
              <div className="bo-editor-toolbar">
                <div className="bo-toolbar-group">
                  <button
                    type="button"
                    className="bo-toolbar-btn"
                    onClick={() => handleEditorCommand('bold')}
                    title="Gras"
                  >
                    <Bold size={18} />
                  </button>
                  <button
                    type="button"
                    className="bo-toolbar-btn"
                    onClick={() => handleEditorCommand('italic')}
                    title="Italique"
                  >
                    <Italic size={18} />
                  </button>
                  <button
                    type="button"
                    className="bo-toolbar-btn"
                    onClick={() => handleEditorCommand('underline')}
                    title="Souligné"
                  >
                    <Underline size={18} />
                  </button>
                </div>

                <div className="bo-toolbar-separator"></div>

                <div className="bo-toolbar-group">
                  <button
                    type="button"
                    className="bo-toolbar-btn"
                    onClick={() => handleEditorCommand('insertUnorderedList')}
                    title="Liste à puces"
                  >
                    <List size={18} />
                  </button>
                  <button
                    type="button"
                    className="bo-toolbar-btn"
                    onClick={() => handleEditorCommand('insertOrderedList')}
                    title="Liste numérotée"
                  >
                    <ListOrdered size={18} />
                  </button>
                </div>

                <div className="bo-toolbar-separator"></div>

                <div className="bo-toolbar-group">
                  <button
                    type="button"
                    className="bo-toolbar-btn"
                    onClick={() => handleEditorCommand('justifyLeft')}
                    title="Aligner à gauche"
                  >
                    <AlignLeft size={18} />
                  </button>
                  <button
                    type="button"
                    className="bo-toolbar-btn"
                    onClick={() => handleEditorCommand('justifyCenter')}
                    title="Centrer"
                  >
                    <AlignCenter size={18} />
                  </button>
                  <button
                    type="button"
                    className="bo-toolbar-btn"
                    onClick={() => handleEditorCommand('justifyRight')}
                    title="Aligner à droite"
                  >
                    <AlignRight size={18} />
                  </button>
                </div>

                <div className="bo-toolbar-separator"></div>

                <div className="bo-toolbar-group">
                  <button
                    type="button"
                    className="bo-toolbar-btn"
                    onClick={() => {
                      const url = prompt('Entrez l\'URL du lien:');
                      if (url) handleEditorCommand('createLink', url);
                    }}
                    title="Insérer un lien"
                  >
                    <LinkIcon size={18} />
                  </button>
                  <button
                    type="button"
                    className="bo-toolbar-btn"
                    onClick={() => editorImageInputRef.current?.click()}
                    title="Insérer une image"
                  >
                    <ImageIcon size={18} />
                  </button>
                  <input
                    ref={editorImageInputRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'editor')}
                    style={{ display: 'none' }}
                  />
                </div>

                <div className="bo-toolbar-separator"></div>

                <div className="bo-toolbar-group">
                  <button
                    type="button"
                    className="bo-toolbar-btn"
                    onClick={() => handleEditorCommand('formatBlock', '<h2>')}
                    title="Titre 2"
                  >
                    H2
                  </button>
                  <button
                    type="button"
                    className="bo-toolbar-btn"
                    onClick={() => handleEditorCommand('formatBlock', '<h3>')}
                    title="Titre 3"
                  >
                    H3
                  </button>
                  <button
                    type="button"
                    className="bo-toolbar-btn"
                    onClick={() => handleEditorCommand('formatBlock', '<blockquote>')}
                    title="Citation"
                  >
                    <Quote size={18} />
                  </button>
                </div>
              </div>

              <div
                ref={contentEditorRef}
                className={`bo-editor-content ${validationErrors.content ? 'bo-input-error' : ''}`}
                contentEditable
                onInput={handleContentChange}
                placeholder="Commencez à écrire votre article..."
              />
            </div>
            {validationErrors.content && (
              <span className="bo-error-message">{validationErrors.content}</span>
            )}
          </div>
        </div>

        <div className="bo-edit-sidebar">
          {/* Statut */}
          <div className="bo-sidebar-section">
            <h3 className="bo-sidebar-title">
              <Settings size={18} />
              Paramètres de publication
            </h3>

            <div className="bo-form-group">
              <label className="bo-form-label">Statut</label>
              <select
                className="bo-form-select"
                value={formData.status}
                onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
              >
                <option value="draft">Brouillon</option>
                <option value="published">Publié</option>
                <option value="scheduled">Programmé</option>
              </select>
            </div>

            <div className="bo-form-group">
              <label className="bo-form-label">Date de publication</label>
              <input
                type="date"
                className="bo-form-input"
                value={formData.publish_date}
                onChange={(e) => setFormData(prev => ({ ...prev, publish_date: e.target.value }))}
              />
            </div>

            <div className="bo-form-group">
              <label className="bo-form-label">Temps de lecture</label>
              <input
                type="text"
                className="bo-form-input"
                value={formData.read_time}
                onChange={(e) => setFormData(prev => ({ ...prev, read_time: e.target.value }))}
                placeholder="5 min read"
              />
            </div>
          </div>

          {/* Auteur — champ masqué (auteur par défaut assigné automatiquement) */}

          {/* Catégorie */}
          <div className="bo-sidebar-section">
            <h3 className="bo-sidebar-title">
              <Tag size={18} />
              Catégorie *
            </h3>
            <div className="bo-form-group">
              <select
                className={`bo-form-select ${validationErrors.category_id ? 'bo-input-error' : ''}`}
                value={formData.category_id}
                onChange={(e) => setFormData(prev => ({ ...prev, category_id: e.target.value }))}
              >
                <option value="">Sélectionner une catégorie</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>{category.name}</option>
                ))}
              </select>
              {validationErrors.category_id && (
                <span className="bo-error-message">{validationErrors.category_id}</span>
              )}
            </div>
          </div>

          {/* Langue */}
          <div className="bo-sidebar-section">
            <h3 className="bo-sidebar-title">
              <Globe size={18} />
              Langue *
            </h3>
            <div className="bo-form-group">
              <select
                className={`bo-form-select ${validationErrors.language_id ? 'bo-input-error' : ''}`}
                value={formData.language_id}
                onChange={(e) => setFormData(prev => ({ ...prev, language_id: e.target.value }))}
              >
                <option value="">Sélectionner une langue</option>
                {languages.map(language => (
                  <option key={language.id} value={language.id}>{language.name}</option>
                ))}
              </select>
              {validationErrors.language_id && (
                <span className="bo-error-message">{validationErrors.language_id}</span>
              )}
            </div>
          </div>

          {/* Tags */}
          <div className="bo-sidebar-section">
            <h3 className="bo-sidebar-title">
              <Tag size={18} />
              Tags
            </h3>
            <div className="bo-tags-input">
              <input
                type="text"
                className="bo-form-input"
                value={currentTag}
                onChange={(e) => setCurrentTag(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
                placeholder="Ajouter un tag..."
              />
              <button
                type="button"
                className="bo-btn bo-btn-sm"
                onClick={handleAddTag}
              >
                <Plus size={16} />
              </button>
            </div>
            <div className="bo-tags-list">
              {formData.tags.map(tag => (
                <span key={tag} className="bo-tag">
                  {tag}
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="bo-tag-remove"
                  >
                    <X size={14} />
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* SEO */}
          <div className="bo-sidebar-section">
            <h3 className="bo-sidebar-title">
              <Search size={18} />
              SEO
            </h3>
            <div className="bo-form-group">
              <label className="bo-form-label">Meta Description</label>
              <textarea
                className="bo-form-textarea"
                value={formData.meta_description}
                onChange={(e) => setFormData(prev => ({ ...prev, meta_description: e.target.value }))}
                placeholder="Description pour les moteurs de recherche..."
                rows={3}
              />
            </div>
            <div className="bo-form-group">
              <label className="bo-form-label">Meta Keywords</label>
              <input
                type="text"
                className="bo-form-input"
                value={formData.meta_keywords}
                onChange={(e) => setFormData(prev => ({ ...prev, meta_keywords: e.target.value }))}
                placeholder="mots, clés, séparés, par, virgules"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Vue Prévisualisation
  const renderPreviewView = () => {
    const previewArticle = selectedArticle || formData;
    const author = authors.find(a => a.id === parseInt(previewArticle.author_id));
    const category = categories.find(c => c.id === parseInt(previewArticle.category_id));

    return (
      <div className="bo-preview-view">
        <div className="bo-preview-header">
          <button className="bo-btn bo-btn-back" onClick={() => setCurrentView(selectedArticle ? 'list' : 'edit')}>
            <X size={20} />
          </button>
          <h2>Prévisualisation</h2>
          {!selectedArticle && (
            <button className="bo-btn bo-btn-publish" onClick={() => setCurrentView('edit')}>
              <Edit2 size={20} />
              Continuer l'édition
            </button>
          )}
        </div>

        <div className="bo-preview-container">
          <article className="bo-preview-article">
            <div className="bo-preview-article-header" style={{ backgroundImage: `url(${previewArticle.image})` }}>
              <div className="bo-preview-article-overlay">
                <div className="container">
                  <div className="bo-preview-article-meta">
                    <span className="bo-preview-category">{category?.name || 'Sans catégorie'}</span>
                    {getStatusBadge(previewArticle.status)}
                  </div>
                  <h1>{previewArticle.title}</h1>
                  <p className="bo-preview-excerpt">{previewArticle.excerpt}</p>

                  <div className="bo-preview-meta-items">
                    <div className="bo-preview-meta-item">
                      <Calendar size={18} />
                      {formatDate(previewArticle.publish_date)}
                    </div>
                    <div className="bo-preview-meta-item">
                      <Clock size={18} />
                      {previewArticle.read_time}
                    </div>
                    <div className="bo-preview-meta-item">
                      <Eye size={18} />
                      {previewArticle.views || 0} vues
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bo-preview-content">

        {previewArticle.imageArticle && (
          <div className="bo-preview-article-image">
            <img src={previewArticle.imageArticle} alt={previewArticle.title} />
          </div>
        )}

        {false && author && (
                <div className="bo-preview-author">
                  {author.avatar && (
                    <img src={author.avatar} alt={author.name} />
                  )}
                  <div className="bo-preview-author-info">
                    <h4>{author.name}</h4>
                    <p>{author.bio}</p>
                  </div>
                </div>
              )}

              <div
                className="bo-preview-body"
                dangerouslySetInnerHTML={{ __html: previewArticle.content }}
              />

              {previewArticle.tags && previewArticle.tags.length > 0 && (
                <div className="bo-preview-tags">
                  <h4><Tag size={18} /> Tags</h4>
                  <div className="bo-preview-tags-list">
                    {previewArticle.tags.map((tag, index) => {
                      const tagName = typeof tag === 'string' ? tag : tag.name;
                      return <span key={index} className="bo-preview-tag">{tagName}</span>;
                    })}
                  </div>
                </div>
              )}

              <div className="bo-preview-share">
                <h4><Globe size={18} /> Partager sur</h4>
                <div className="bo-preview-share-buttons">
                  <button className="bo-share-btn bo-facebook">
                    <Facebook size={18} />
                    Facebook
                  </button>
                  <button className="bo-share-btn bo-twitter">
                    <Twitter size={18} />
                    Twitter
                  </button>
                  <button className="bo-share-btn bo-linkedin">
                    <Linkedin size={18} />
                    LinkedIn
                  </button>
                </div>
              </div>
            </div>
          </article>
        </div>
      </div>
    );
  };

  return (
    <div className="blog-backoffice">
      <header className="bo-header">
        <div className="bo-header-content">
          <div className="bo-header-brand">
            <div className="bo-brand-icon">
              <FileText size={28} />
            </div>
            <div>
              <h1>Backoffice Blog</h1>
              <p>Gestion des articles et du contenu</p>
            </div>
          </div>

          <nav className="bo-header-nav">
  <button
    className={currentView === 'list' && !showUserManagement ? 'active' : ''}
    onClick={() => {
      setShowUserManagement(false);
        setShowAnalytics(false);
      resetForm();
      setCurrentView('list');
    }}
  >
    <FileText size={20} />
    Articles
  </button>
  <button
    className={currentView === 'edit' && !showUserManagement ? 'active' : ''}
    onClick={() => {
      setShowUserManagement(false);
        setShowAnalytics(false);
      resetForm();
      setCurrentView('edit');
    }}
  >
    <Edit2 size={20} />
    Édition
  </button>
  <button
    className={showAnalytics ? 'active' : ''}
    onClick={() => {
      setShowUserManagement(false);
      setShowAnalytics(true);
    }}
  >
    <BarChart3 size={20} />
    Statistiques
  </button>
  <button
    className={showUserManagement ? 'active' : ''}
    onClick={() => {
      setShowUserManagement(true);
      setShowAnalytics(false);
    }}
  >
    <Users size={20} />
    Utilisateurs
  </button>
</nav>
        </div>
      </header>

      <main className="bo-main-content">
    {showSuccessMessage && (
      <div className="bo-success-message">
        <Check size={20} />
        {showSuccessMessage}
      </div>
    )}

    {showErrorMessage && (
      <div className="bo-error-message-banner">
        <AlertCircle size={20} />
        {showErrorMessage}
        <button onClick={() => setShowErrorMessage('')}>
          <X size={16} />
        </button>
      </div>
    )}

    {showUserManagement ? (
       <UserManagement />
     ) : showAnalytics ? (
       <AnalyticsDashboard />
     ) : (
      <>
        {currentView === 'list' && renderListView()}
        {currentView === 'edit' && renderEditView()}
        {currentView === 'preview' && renderPreviewView()}
      </>
    )}
  </main>

      {/* Modal de traduction */}
      {showTranslationModal && (
        <div className="bo-translation-modal-overlay" onClick={() => setShowTranslationModal(false)}>
          <div className="bo-translation-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bo-translation-modal-header">
              <h3>
                <Languages size={24} />
                Traduire l'article
              </h3>
              <button className="bo-btn-close" onClick={() => setShowTranslationModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="bo-translation-modal-body">
              <p className="bo-translation-modal-info">
                Vous allez créer une traduction manuelle de l'article "{translatingArticle?.title}".
                L'éditeur s'ouvrira avec le contenu original que vous pourrez traduire.
              </p>

              <div className="bo-form-group">
                <label className="bo-form-label">Langue de traduction</label>
                <select
                  value={targetLanguage}
                  onChange={(e) => setTargetLanguage(e.target.value)}
                  className="bo-translation-select"
                >
                  <option value="">Sélectionner une langue</option>
                  {languages
                    .filter(lang => lang.id !== translatingArticle?.language_id)
                    .map(lang => (
                      <option key={lang.id} value={lang.id}>
                        {lang.name}
                      </option>
                    ))}
                </select>
              </div>

              <div className="bo-translation-info-box">
                <h4>📝 Ce qui sera copié :</h4>
                <ul>
                  <li>✅ Image, catégorie, tags</li>
                  <li>✅ Auteur et informations de base</li>
                  <li>✅ Date et paramètres de publication</li>
                </ul>
                <h4>✏️ Ce que vous devrez traduire :</h4>
                <ul>
                  <li>📄 Titre de l'article</li>
                  <li>📄 Extrait (résumé)</li>
                  <li>📄 Contenu complet</li>
                </ul>
              </div>
            </div>

            <div className="bo-translation-modal-footer">
              <button className="bo-btn bo-btn-back" onClick={() => setShowTranslationModal(false)}>
                Annuler
              </button>
              <button
                className="bo-btn bo-btn-publish"
                onClick={startManualTranslation}
                disabled={!targetLanguage}
              >
                <Edit2 size={20} />
                Commencer la traduction
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogBackoffice;
